import {EntityServiceContract} from "@shared/contracts/entity-service.contract";
import {Store} from "@ngrx/store";
import {catchError, EMPTY, finalize, first, map, Observable, of, switchMap, tap} from "rxjs";
import {ModelFactory} from "@shared/factories/model.factory";
import {EntityMap, EntityMapOne, Predicate, Update} from "@ngrx/entity";
import {EntityFeatureNameEnum, EntityStore} from "@shared/contracts/entity-store.contract";
import {ModelConstructor} from "@shared/models/base.model";
import {isPlainObject} from "lodash-es";

type EntityLoadingController = {
  setLoading(value: boolean): void;
}

type EntityTrackingOptions = {
  loadingController?: EntityLoadingController;
  selectActive?: boolean;
  swallowError?: boolean;
}

type EntityReplaceRefreshPlan<BE> = {
  mode: 'replace';
  source$: (id: string) => Observable<BE>;
}

type EntityPatchRefreshPlan<BE> = {
  mode: 'patch';
  seed$?: (id: string) => Observable<BE | null>;
  patch$: (id: string) => Observable<Partial<BE>>;
}

type EntityRefreshPlan<BE> = EntityReplaceRefreshPlan<BE> | EntityPatchRefreshPlan<BE>

export abstract class EntityService<
  EntityFeature extends EntityFeatureNameEnum,
  BE extends Record<string, any>,
  FE extends BE,
  Factory extends ModelFactory<BE, FE> = ModelFactory<BE, FE>
> implements EntityServiceContract<BE, FE> {
  protected readonly rawEntities$: Observable<BE[]> = this.store.select(this.entityStore.selectAllEntity);

  readonly entities$: Observable<FE[]> = this.rawEntities$.pipe(
    map(entities => entities ? entities.map(entity => this.toViewEntity(entity)) : []),
  );

  protected readonly rawActiveEntity$: Observable<BE | null> = this.store.select(this.entityStore.selectSelectedEntity);

  readonly activeEntity$: Observable<FE> = this.rawActiveEntity$.pipe(
    map(entity => entity ? this.toViewEntity(entity) : null)
  )

  abstract get factory(): Factory

  protected constructor(
    protected readonly store: Store,
    protected readonly entityStore: EntityStore<EntityFeature, BE, FE>
  ) {
  }

  find$(id: string): Observable<FE> {
    return this.findRaw$(id).pipe(
      map(item => item ? this.toViewEntity(item) : null)
    );
  }

  getByIds$(ids: string[], keepDuplicate: boolean = true): Observable<FE[]> {
    const searchIds = !keepDuplicate ? [...new Set(ids)] : ids

    return this.store.select(this.entityStore.selectEntitiesByIds(searchIds)).pipe(
      map(items => items ? items.map(item => this.factory.create(item)) : [])
    );
  }

  readonly dispatchSetEntities = (entities: BE[]): void => {
    this.store.dispatch(this.entityStore.setEntitiesAction({
      entities: this.toStoreEntities(entities),
    }))
  }

  readonly dispatchLoadEntitiesAction = (entities: BE[]): void => {
    this.store.dispatch(this.entityStore.loadEntitiesAction({
      entities: this.toStoreEntities(entities),
    }))
  }

  readonly dispatchAddEntityAction = (entity: BE): void => {
    this.store.dispatch(this.entityStore.addEntityAction({
      entity: this.toStoreEntity(entity),
    }))
  }

  readonly dispatchSetEntityAction = (entity: BE): void => {
    this.store.dispatch(this.entityStore.setEntityAction({
      entity: this.toStoreEntity(entity),
    }))
  }

  readonly dispatchUpsertEntityAction = (entity: BE): void => {
    this.store.dispatch(this.entityStore.upsertEntityAction({
      entity: this.toStoreEntity(entity),
    }))
  }

  readonly dispatchAddEntitiesAction = (entities: BE[]): void => {
    this.store.dispatch(this.entityStore.addEntitiesAction({
      entities: this.toStoreEntities(entities),
    }))
  }

  readonly dispatchUpsertEntitiesAction = (entities: BE[]): void => {
    this.store.dispatch(this.entityStore.upsertEntitiesAction({
      entities: this.toStoreEntities(entities),
    }))
  }

  readonly dispatchUpdateEntityAction = (update: Update<BE>): void => {
    this.store.dispatch(this.entityStore.updateEntityAction({
      update: this.toStoreUpdate(update),
    }))
  }

  readonly dispatchUpdateEntitiesAction = (updates: Update<BE>[]): void => {
    this.store.dispatch(this.entityStore.updateEntitiesAction({
      updates: updates.map(update => this.toStoreUpdate(update)),
    }))
  }

  readonly dispatchMapEntityAction = (entityMap: EntityMapOne<BE>): void => {
    this.store.dispatch(this.entityStore.mapEntityAction({
      entityMap: {
        ...entityMap,
        map: (entity: BE) => this.toStoreEntity(entityMap.map(entity)),
      },
    }))
  }

  readonly dispatchMapEntitiesAction = (entityMap: EntityMap<BE>): void => {
    this.store.dispatch(this.entityStore.mapEntitiesAction({
      entityMap: (entity: BE) => this.toStoreEntity(entityMap(entity)),
    }))
  }

  readonly dispatchDeleteEntityAction = (id: string): void => {
    this.store.dispatch(this.entityStore.deleteEntityAction({id}))
  }

  readonly dispatchDeleteEntitiesAction = (ids: string[]): void => {
    this.store.dispatch(this.entityStore.deleteEntitiesAction({ids}))
  }

  readonly dispatchDeleteEntitiesByPredicateAction = (predicate: Predicate<BE>): void => {
    this.store.dispatch(this.entityStore.deleteEntitiesByPredicateAction({predicate}))
  }

  readonly dispatchClearEntitiesAction = (): void => {
    this.store.dispatch(this.entityStore.clearEntitiesAction())
  }

  readonly dispatchSelectEntityAction = (selectedEntityId: string): void => {
    this.store.dispatch(this.entityStore.selectEntityAction({selectedEntityId}))
  }

  readonly dispatchAddOrUpdateEntityAction = (entity: BE): void => {
    this.store.dispatch(this.entityStore.upsertEntityAction({
      entity: this.toStoreEntity(entity),
    }))
  }

  readonly dispatchAddOrUpdateThenSelectEntityAction = (entity: BE): void => {
    this.dispatchAddOrUpdateEntityAction(entity)
    this.dispatchSelectEntityAction(entity.id)
  }

  readonly dispatchSetActiveEntityAction = (entity: BE): void => {
    this.dispatchAddOrUpdateThenSelectEntityAction(entity)
  }

  protected findOrLoad$ (id: string, loader: (id: string) => Observable<FE>): Observable<FE> {
    return this.find$(id).pipe(
      first(),
      switchMap(entity => entity ? of(entity) : loader(id))
    )
  }

  protected findRaw$(id: string): Observable<BE | null> {
    return this.store.select(this.entityStore.selectEntity(id));
  }

  protected findRawOrLoad$ (id: string, loader: (id: string) => Observable<BE | null>): Observable<BE | null> {
    return this.findRaw$(id).pipe(
      first(),
      switchMap(entity => entity ? of(entity) : loader(id))
    )
  }

  protected mergeEntityPatch (
    currentEntity: BE | null,
    entityPatch: Partial<BE> | null,
  ): BE {
    return this.toStoreEntity(
      this.mergePatchValue(
        (currentEntity as BE) ?? {} as BE,
        entityPatch ?? {},
      ) as BE,
    )
  }

  protected trackEntity$ (
    source$: Observable<BE>,
    options: EntityTrackingOptions = {},
  ): Observable<FE> {
    const {loadingController, selectActive = false, swallowError = false} = options

    loadingController?.setLoading(true)

    const tracked$ = source$.pipe(
      map(entity => {
        const storeEntity = this.toStoreEntity(entity)

        return {
          storeEntity,
          viewEntity: this.toViewEntity(storeEntity),
        }
      }),
      tap(({storeEntity}) => {
        if (selectActive) {
          this.dispatchSetActiveEntityAction(storeEntity)

          return
        }

        this.dispatchUpsertEntityAction(storeEntity)
      }),
      map(({viewEntity}) => viewEntity),
      finalize(() => {
        loadingController?.setLoading(false)
      }),
    )

    if (!swallowError) {
      return tracked$
    }

    return tracked$.pipe(
      catchError(e => {
        console.error(e)

        return EMPTY
      }),
    )
  }

  protected trackEntityPatch$ (
    currentEntity$: Observable<BE | null>,
    entityPatch$: Observable<Partial<BE>>,
    options: EntityTrackingOptions = {},
  ): Observable<FE> {
    return this.trackEntity$(
      currentEntity$.pipe(
        switchMap(currentEntity =>
          entityPatch$.pipe(
            map(entityPatch => this.mergeEntityPatch(currentEntity, entityPatch)),
          ),
        ),
      ),
      options,
    )
  }

  protected refreshEntityByPlan$(
    id: string,
    plan: EntityRefreshPlan<BE>,
    options: EntityTrackingOptions = {},
  ): Observable<FE> {
    if (plan.mode === 'replace') {
      return this.trackEntity$(plan.source$(id), options)
    }

    return this.trackEntityPatch$(
      plan.seed$
        ? this.findRawOrLoad$(id, plan.seed$)
        : this.findRaw$(id).pipe(first()),
      plan.patch$(id),
      options,
    )
  }

  private mergePatchValue<T>(currentValue: T, patchValue: unknown): T {
    if (patchValue === undefined) {
      return currentValue
    }

    if (Array.isArray(patchValue)) {
      if (!Array.isArray(currentValue)) {
        return patchValue as unknown as T
      }

      return this.mergeEntityArrayPatch(
        currentValue as unknown as Record<string, unknown>[],
        patchValue as Record<string, unknown>[],
      ) as unknown as T
    }

    if (this.isMergeableObject(currentValue) && this.isMergeableObject(patchValue)) {
      const result: Record<string, unknown> = {
        ...(currentValue as Record<string, unknown>),
      }

      Object.entries(patchValue).forEach(([key, value]) => {
        result[key] = this.mergePatchValue(
          (currentValue as Record<string, unknown>)?.[key],
          value,
        )
      })

      return result as T
    }

    return patchValue as T
  }

  private mergeEntityArrayPatch(
    currentItems: Record<string, unknown>[],
    patchItems: Record<string, unknown>[],
  ): Record<string, unknown>[] {
    if (!patchItems.every(item => this.hasEntityId(item))) {
      return patchItems
    }

    const currentItemsById = new Map(
      currentItems
        .filter(item => this.hasEntityId(item))
        .map(item => [item.id as string, item]),
    )

    return patchItems.map(item => {
      const currentItem = currentItemsById.get(item.id as string)

      if (!currentItem) {
        return item
      }

      return this.mergePatchValue(currentItem, item)
    })
  }

  private hasEntityId(item: Record<string, unknown> | null | undefined): item is Record<string, unknown> & { id: string } {
    return typeof item?.id === 'string' && item.id.length > 0
  }

  private toStoreEntities(entities: BE[]): BE[] {
    return entities.map(entity => this.toStoreEntity(entity))
  }

  private toViewEntity(entity: BE): FE {
    return this.factory.create(entity)
  }

  private toStoreEntity<T extends Record<string, any>>(entity: T): T {
    return this.dehydrateStoreValue(entity, new WeakMap<object, unknown>()) as T
  }

  private toStoreUpdate(update: Update<BE>): Update<BE> {
    return {
      ...update,
      changes: this.toStoreEntity(update.changes),
    }
  }

  private dehydrateStoreValue(value: unknown, seen: WeakMap<object, unknown>): unknown {
    if (Array.isArray(value)) {
      return value.map(item => this.dehydrateStoreValue(item, seen))
    }

    if (value instanceof Date) {
      return new Date(value.getTime())
    }

    if (!this.isDehydratableObject(value)) {
      return value
    }

    const cachedValue = seen.get(value)

    if (cachedValue) {
      return cachedValue
    }

    const result: Record<string, unknown> = {}
    seen.set(value, result)

    Object.entries(value).forEach(([key, currentValue]) => {
      result[key] = this.dehydrateStoreValue(currentValue, seen)
    })

    return result
  }

  private isDehydratableObject(value: unknown): value is Record<string, unknown> {
    return isPlainObject(value) || value instanceof ModelConstructor
  }

  private isMergeableObject(value: unknown): value is Record<string, unknown> {
    return isPlainObject(value)
  }

  trackEntities$ (source$: Observable<BE[]>) {
    return source$.pipe(
      map(entities => {
        const storeEntities = this.toStoreEntities(entities)

        return {
          storeEntities,
          viewEntities: storeEntities.map(entity => this.toViewEntity(entity)),
        }
      }),
      tap(({storeEntities}) => this.dispatchSetEntities(storeEntities)),
      map(({viewEntities}) => viewEntities),
      catchError(e => {
        return EMPTY
      })
    )
  }
}
